import { computed, observable, action, makeObservable, runInAction } from 'mobx'
import Wkt from 'wicket/wicket'
import _ from 'lodash'
import api from 'api'
import Leaflet from 'leaflet'
import Boundary from './Boundary'
import Geopolygon from './Geopolygon'
import SavedMarker from './SavedMarker'
import RegionAnalysis from './RegionAnalysis'
import EmptyTargetGroupAnalysis from './EmptyTargetGroupAnalysis'
import ProcessingTargetGroupAnalysis from './ProcessingTargetGroupAnalysis'
import { asyncStatuses, selectionTypes, LANDSCAN_LATEST_YEAR, ADDITIONAL_INFO_DEFAULT_INPUT } from 'constants.js'
import { catchError } from './ErrorStore'
import { uiStore, dataStore, selectionStore } from './UIStore'
import FileSaver from 'file-saver'
import i18next from '../i18n'
import { move } from '@dnd-kit/helpers'
import { debounce } from 'lodash'

const GEOPOLYGON_PROPERTIES = ['id', 'targetGroupId', 'name', 'polygon', 'created', 'type', 'color_id', 'color_transparency', 'population_datas', 'shape_status', 'tag_ids', 'additional_info', 'notes', 'contact_detail']
const BOUNDARY_PROPERTIES = ['remote_db_id', 'inner_id', 'name', 'geometry', 'created', 'color_id', 'color_transparency', 'population_datas', 'shape_status', 'tag_ids', 'is_owner', 'additional_info', 'notes', 'contact_detail']
const SAVED_MARKER_PROPERTIES = ['id', 'name', 'latitude', 'longitude', 'description', 'address', 'color', 'created', 'is_general', 'is_competitor', 'is_ally', 'icon_url', 'types', 'place_id', 'additional_info', 'notes', 'contact_detail', 'shape_status', 'tag_ids']

// Represents a user's target group, containing markers, geopolygons,
// and boundaries
class TargetGroup {
  @observable name
  @observable pk
  @observable visible
  @observable owner
  @observable is_owner
  @observable error
  @observable share_type
  @observable expanded
  @observable geopolygons = []
  @observable boundaries = []
  @observable zipCodeBoundaries = []
  @observable savedMarkers = []
  @observable loading = true
  @observable fetchingKml = false
  @observable sidebarCache = new Map()
  @observable renameForm = { active: false, name: '' }
  @observable analysis = new RegionAnalysis()
  @observable geoJSON = null
  @observable enableBoundaries = true
  @observable transparency = 50
  @observable color_id
  @observable isDraggedOver = false
  @observable processing = false
  @observable modified
  @observable status
  @observable status_color
  @observable tag_ids = []
  @observable.ref population_datas
  @observable.ref dissolved_population_datas
  @observable additional_info = []
  @observable notes = ''
  @observable contact_detail = { given_name: '', surname: '', email: '', address: '', country: '' }

  constructor (
    {
      name,
      pk,
      expanded = false,
      visible = true,
      owner,
      is_owner,
      share_type,
      modified,
      folder_status,
      status_color,
      tag_ids = [],
      additional_info = [],
      notes = '',
      contact_detail = {}
    },
    enableBoundaries = true
  ) {
    makeObservable(this)
    this.analyticsLoading = true
    this.pk = pk
    this.name = name
    this.expanded = expanded
    this.visible = visible
    this.owner = owner
    this.is_owner = is_owner
    this.share_type = share_type
    this.enableBoundaries = enableBoundaries !== undefined ? enableBoundaries : true
    this.modified = modified
    this.status = folder_status
    this.status_color = status_color
    this.tag_ids = tag_ids
    this.additional_info = additional_info
    this.notes = notes
    this.contact_detail = { given_name: '', surname: '', email: '', phone: '', address: '', country: '', ...contact_detail }
  }

  startRename () {
    this.error = null
    this.renameForm.active = true
    this.renameForm.name = this.name
  }

  endRename () {
    this.renameForm.active = false
    this.renameForm.name = ''
  }

  getTypeLabel () {
    return 'Folder'
  }

  findAdditionalInfoIndex (id) {
    return this.additional_info.findIndex(item => Number(item.info_item) === Number(id))
  }

  getAdditionalInfoItemValue = (id) => {
    const item = this.additional_info.find(item => item.id === id)
    if (item) {
      return item.input
    }
    return null
  }

  @action deleteAdditionalInfoItem = (id) => {
    this.additional_info = this.additional_info.filter(item => item.id !== id)
    this.saveAdditionalInfo()
  }

  @action updateAdditionalInfoItem = (id, field, value) => {
    const index = this.findAdditionalInfoIndex(id)

    if (index === -1) {
      const definition = dataStore.userAdditionalInfoItems.find(item => item.id === id)
      if (!definition) return

      this.additional_info.push({
        id: definition.id,
        order: this.additional_info.length + 1,
        info_item: definition.id,
        label: definition.label,
        type: definition.type,
        input: {
          ...ADDITIONAL_INFO_DEFAULT_INPUT[definition.type],
          [field]: value
        }
      })
    } else {
      const item = this.additional_info[index]
      this.additional_info[index] = {
        ...item,
        input: {
          ...item.input,
          [field]: value
        }
      }
    }

    this.debouncedSaveAdditionalInfo(id)
  }

  @action updateAdditionalInfoItemType = (id, type, label) => {
    const index = this.additional_info.findIndex(item => item.id === id)
    if (index === -1) return

    const item = this.additional_info[index]
    if (item.type === type) return

    this.additional_info[index] = {
      ...item,
      type,
      label,
      input: {
        ...ADDITIONAL_INFO_DEFAULT_INPUT[type],
        title: item.input ? item.input.title : null
      }
    }

    this.debouncedSaveAdditionalInfo(id)
  }

  @action updateNotes = (value) => {
    this.notes = value
    this.debouncedSaveAdditionalInfo()
  }

  debouncedSaveAdditionalInfo = debounce((id) => {
    this.saveAdditionalInfo(id)
  }, 500)

  @action updateContactDetail = (field, value) => {
    this.contact_detail = { ...this.contact_detail, [field]: value }
    this.debouncedSaveContactDetail()
  }

  debouncedSaveContactDetail = debounce(() => {
    this.saveContactDetail()
  }, 800)

  @catchError async saveContactDetail () {
    await api.postTargetGroupContactDetail(this.pk, this.contact_detail)
  }

  @catchError async addAdditionalInfoItem (item) {
    runInAction(() => {
      this.additional_info.push(item)
    })
    await this.saveAdditionalInfo()
  }

  @computed get hasExceededMarkerLimit () {
    return uiStore.user.limits.max_markers <= this.markersCount
  }

  @computed get hasExceededRegionsLimit () {
    // Starters are gated at the +New button, so the inner menus don't re-check.
    if (uiStore.user && uiStore.user.isStarter) {
      return false
    }
    // return uiStore.user.limits.max_regions <= this.regionsCount
    return uiStore.userTotalRegionsCount + 1 > uiStore.user.limits.max_regions || this.regionsCount + 1 > uiStore.user.limits.max_regions
  }

  @computed get regions () {
    const res = _.concat(...this.geopolygons, ...this.boundaries, ...this.zipCodeBoundaries)

    res.sort((a, b) => {
      const timeA = a && a.created ? new Date(a.created).getTime() || 0 : 0
      const timeB = b && b.created ? new Date(b.created).getTime() || 0 : 0
      return timeA - timeB
    })

    return res
  }

  isExceededLimit (type) {
    if (type === selectionTypes.SAVED_MARKER) {
      return this.hasExceededMarkerLimit
    } else {
      return this.hasExceededRegionsLimit
    }
  }

  @computed get markersCount () {
    return this.savedMarkers.length
  }

  @computed get regionsCount () {
    const geos = (this.geopolygons || []).length
    const bounds = (this.boundaries || []).length
    const zips = (this.zipCodeBoundaries || this.zipCodes || this.zip_code_boundaries || []).length
    return geos + bounds + zips
  }

  @computed get itemsCount () {
    const geos = (this.geopolygons || []).length
    const bounds = (this.boundaries || []).length
    const zips = (this.zipCodeBoundaries || this.zipCodes || this.zip_code_boundaries || []).length
    const markers = (this.savedMarkers || this.markers || []).length
    return geos + bounds + zips + markers
  }

  @catchError async saveAdditionalInfo (id) {
    const data = this.additional_info.find(item => item.info_item === id)
    await api.postTargetGroupAdditionalInfo(this.pk, data)
  }

  @catchError async rename (name) {
    await api.patchTargetGroup(this.pk, { name })
  }

  @catchError async updateStatus (folderStatus) {
    const response = await api.patchTargetGroup(this.pk, { folder_status: folderStatus.id })
    runInAction(() => {
      this.status = response.data.folder_status
      this.status_color = response.data.status_color
    })
  }

  @catchError
  async updateTags (tagId) {
    runInAction(() => {
      if (!this.tag_ids.includes(tagId)) this.tag_ids.push(tagId)
    })
    await api.postTargetGroupTags(this.pk, { tag_ids: [tagId] })
  }

  @catchError
  async deleteTag (tagId) {
    runInAction(() => {
      this.tag_ids = this.tag_ids.filter(id => tagId !== id)
    })
    await api.deleteTargetGroupTag(this.pk, tagId)
  }

  @catchError async deleteTargetGroup () {
    await api.deleteTargetGroup(this.pk)
  }

  async deleteGeopolygon (geopolygon, options = {}) {
    const geoId = geopolygon && (geopolygon.id || geopolygon)
    runInAction(() => {
      this.geopolygons = (this.geopolygons || []).filter(g => g !== geopolygon && g.id !== geoId)
    })
    if (typeof geopolygon.deleteGeopolygon === 'function') {
      await geopolygon.deleteGeopolygon()
    }
    await this.loadGeometries()
    await this.loadBoundaries()
    runInAction(() => {
      this.geopolygons = (this.geopolygons || []).filter(g => g !== geopolygon && g.id !== geoId)
    })
  }

  async deleteSavedMarker (savedMarker) {
    const markerId = savedMarker && (savedMarker.id || savedMarker)
    runInAction(() => {
      this.savedMarkers = (this.savedMarkers || []).filter(m => m !== savedMarker && m.id !== markerId)
    })
    if (typeof savedMarker.deleteSavedMarker === 'function') {
      await savedMarker.deleteSavedMarker()
    }
    await this.loadSavedMarkers()
    runInAction(() => {
      this.savedMarkers = (this.savedMarkers || []).filter(m => m !== savedMarker && m.id !== markerId)
    })
  }

  async deleteBoundary (boundary, options = {}) {
    const { keepMarkers } = options
    if (!keepMarkers) {
      await this.deleteCurrentItemMarkersIfActive(boundary)
    }
    const innerId = boundary && (boundary.inner_id !== undefined ? boundary.inner_id : (boundary.id || boundary))
    const remoteId = boundary && (boundary.remote_db_id !== undefined ? boundary.remote_db_id : (boundary.id || boundary))
    runInAction(() => {
      this.boundaries = (this.boundaries || []).filter(b => b !== boundary && b.inner_id !== innerId && b.remote_db_id !== remoteId && b.id !== innerId)
      this.zipCodeBoundaries = (this.zipCodeBoundaries || []).filter(b => b !== boundary && b.inner_id !== innerId && b.remote_db_id !== remoteId && b.id !== innerId)
    })
    if (typeof boundary.deleteBoundary === 'function') {
      await boundary.deleteBoundary()
    }
    if (boundary && boundary.isZipCode) {
      await this.loadZipCodeBoundaries()
    } else {
      await this.loadBoundaries()
    }
    await this.loadGeometries()
    runInAction(() => {
      this.boundaries = (this.boundaries || []).filter(b => b !== boundary && b.inner_id !== innerId && b.remote_db_id !== remoteId && b.id !== innerId)
      this.zipCodeBoundaries = (this.zipCodeBoundaries || []).filter(b => b !== boundary && b.inner_id !== innerId && b.remote_db_id !== remoteId && b.id !== innerId)
    })
  }

  @catchError async share () {
    await api.postTargetGroupShare(this.pk, {
      share_type: 'license_group'
    })
  }

  @catchError async unshare () {
    await api.postTargetGroupShare(this.pk, {
      share_type: 'none'
    })
  }

  bounds () {
    const bounds = Leaflet.latLngBounds()
    _.each(this.geopolygons, item => { bounds.extend(item.bounds()) })
    _.each(this.boundaries, item => { bounds.extend(item.bounds()) })
    _.each(this.zipCodeBoundaries, item => { bounds.extend(item.bounds()) })
    const regionBounds = new Leaflet.LatLngBounds().extend(bounds)
    _.each(this.savedMarkers, item => {
      // A marker already inside the regions needs no extra extent. Its 0.5°
      // standalone buffer would otherwise zoom a small shape's folder out.
      // Keep the existing buffer for outside markers and marker-only folders.
      const position = item.position && item.position.latLng
      if (regionBounds.isValid() && position && regionBounds.contains(position)) return
      bounds.extend(item.bounds())
    })

    return bounds
  }

  async load (shouldLoadAnalysis = false) {
    await this.loadGeometries()
    await this.loadBoundaries(null, shouldLoadAnalysis)
    await this.loadSavedMarkers()
    await this.loadZipCodeBoundaries(null, shouldLoadAnalysis)
    this.loading = false
  }

  @catchError async loadGeometries (geopolygonId, taskId = null, cancelToken = null) {
    this.sidebarCache.clear()
    this.analysis = new RegionAnalysis()
    const response = await api.getGeometries(this.pk, { cancelToken })
    const remoteGeopolygons = response.data
    // Isolate per-item failures so one malformed geopolygon can't wipe out
    // the whole list (this.geopolygons would otherwise never get reassigned).
    runInAction(() => {
      this.geopolygons = _.compact(_.map(remoteGeopolygons, remote => {
        try {
          return this.loadGeometry(remote, geopolygonId, taskId)
        } catch (e) {
          console.error('Failed to load geopolygon', remote && remote.id, e)
          return null
        }
      }))
    })
    if (dataStore && dataStore.targetGroups) {
      const realGroup = dataStore.targetGroups.find(g => String(g.pk) === String(this.pk)) ||
        (dataStore.sharedTargetGroups && dataStore.sharedTargetGroups.find(g => String(g.pk) === String(this.pk)))
      if (realGroup && realGroup !== this) {
        runInAction(() => {
          realGroup.geopolygons = this.geopolygons
        })
      }
    }
  }

  loadGeometry (remote, geopolygonId, taskId = null) {
    let local = _.find(this.geopolygons, { id: remote.id })
    const remoteProperties = _.pick(remote, GEOPOLYGON_PROPERTIES)

    if (!local) {
      local = new Geopolygon({ targetGroupId: this.pk, ...remoteProperties, transparency: remoteProperties.color_transparency })// Fire and forget - load analysis in background
    } else {
      // Update existing observable properties manually (extendObservable doesn't update existing observables)
      local.name = remoteProperties.name
      local.created = remoteProperties.created
      local.type = remoteProperties.type
      local.color_id = remoteProperties.color_id
      local.transparency = remoteProperties.color_transparency
      local.population_datas = remoteProperties.population_datas
      local.shape_status = remoteProperties.shape_status
      local.tag_ids = remoteProperties.tag_ids || []
      local.computeGeoJSON(remoteProperties.polygon)
      local.tag_ids = remoteProperties.tag_ids
      local.additional_info = remoteProperties.additional_info
      local.notes = remoteProperties.notes
      local.contact_detail = remoteProperties.contact_detail
      if (remoteProperties.population_datas) {
        local.analysis.load(local.pickPopulationDataForCurrentYear(remoteProperties.population_datas), local.geoJSON)
      }
    }
    if (geopolygonId === local.id && taskId) {
      local.loadGeometryAnalysis(taskId) // Fire and forget - load analysis in background
    }
    return local
  }

  @catchError async loadZipCodeBoundaries (cancelToken = null, shouldLoadAnalysis = false) {
    if (this.enableBoundaries !== false) {
      const response = await api.getTagetGroupZipCodeBoundaries(this.pk, { cancelToken })
      const zipCodeBoundaries = response.data
      runInAction(() => {
        this.zipCodeBoundaries = _.compact(_.map(zipCodeBoundaries, remote => {
          try {
            return this.loadBoundary(remote, true, shouldLoadAnalysis)
          } catch (e) {
            console.error('Failed to load zip code boundary', remote && remote.remote_db_id, e)
            return null
          }
        }))
      })
      if (dataStore && dataStore.targetGroups) {
        const realGroup = dataStore.targetGroups.find(g => String(g.pk) === String(this.pk)) ||
          (dataStore.sharedTargetGroups && dataStore.sharedTargetGroups.find(g => String(g.pk) === String(this.pk)))
        if (realGroup && realGroup !== this) {
          runInAction(() => {
            realGroup.zipCodeBoundaries = this.zipCodeBoundaries
          })
        }
      }
    }
  }

  @catchError async loadBoundaries (cancelToken = null, shouldLoadAnalysis = false) {
    if (this.enableBoundaries) {
      this.sidebarCache.clear()
      this.analysis = new RegionAnalysis()
      const response = await api.getTargetGroupBoundaries(this.pk, { cancelToken })
      const remoteBoundaries = response.data

      runInAction(() => {
        this.boundaries = _.compact(_.map(remoteBoundaries, remote => {
          try {
            return this.loadBoundary(remote, false, shouldLoadAnalysis)
          } catch (e) {
            console.error('Failed to load boundary', remote && remote.remote_db_id, e)
            return null
          }
        }))
      })
      if (dataStore && dataStore.targetGroups) {
        const realGroup = dataStore.targetGroups.find(g => String(g.pk) === String(this.pk)) ||
          (dataStore.sharedTargetGroups && dataStore.sharedTargetGroups.find(g => String(g.pk) === String(this.pk)))
        if (realGroup && realGroup !== this) {
          runInAction(() => {
            realGroup.boundaries = this.boundaries
          })
        }
      }
    }
  }

  loadBoundary (remote, isZipCode = false, shouldLoadAnalysis = false) {
    const list = isZipCode ? this.zipCodeBoundaries : this.boundaries
    let local = _.find(list, b => (remote.inner_id && b.inner_id === remote.inner_id) || (!remote.inner_id && b.remote_db_id === remote.remote_db_id))
    const remoteProperties = _.pick(remote, BOUNDARY_PROPERTIES)

    if (!local) {
      local = new Boundary({ targetGroupId: this.pk, ...remoteProperties, isZipCode })
      if (shouldLoadAnalysis && !remoteProperties.population_datas) {
        local.analysis.loading = true
        local.loadAnalysis() // Fire and forget - load analysis in background
      }
    } else {
      // Update existing observable properties manually (extendObservable doesn't update existing observables)
      local.targetGroupId = this.pk
      local.inner_id = remoteProperties.inner_id
      local.name = remoteProperties.name
      local.created = remoteProperties.created
      local.color_id = remoteProperties.color_id
      local.transparency = remoteProperties.color_transparency !== undefined ? remoteProperties.color_transparency : 50
      local.population_datas = remoteProperties.population_datas
      local.status = remoteProperties.shape_status
      local.shape_status = remoteProperties.shape_status
      local.tag_ids = remoteProperties.tag_ids
      local.additional_info = remoteProperties.additional_info
      local.notes = remoteProperties.notes
      local.contact_detail = remoteProperties.contact_detail
      if (remoteProperties.is_owner !== undefined) local.is_owner = remoteProperties.is_owner
      if (remoteProperties.geometry) {
        const wkt = new Wkt.Wkt()
        local.geoJSON = wkt.read(remoteProperties.geometry).toJson()
      }
      if (remoteProperties.population_datas) {
        local.analysis.load(local.pickPopulationDataForCurrentYear(remoteProperties.population_datas), local.geoJSON)
      }
    }
    local.isZipCode = isZipCode

    return local
  }

  @catchError async loadSavedMarkers (cancelToken = null) {
    const response = await api.getMarkers({ cancelToken })
    const remoteSavedMarkers = _.filter(response.data, {
      target_group: this.pk
    })

    const newSavedMarkers = _.compact(_.map(remoteSavedMarkers, remote => {
      try {
        return this.loadSavedMarker(remote)
      } catch (e) {
        console.error('Failed to load saved marker', remote && remote.id, e)
        return null
      }
    }))

    runInAction(() => {
      this.savedMarkers = newSavedMarkers
    })

    if (dataStore && dataStore.targetGroups) {
      const realGroup = dataStore.targetGroups.find(g => String(g.pk) === String(this.pk)) ||
        (dataStore.sharedTargetGroups && dataStore.sharedTargetGroups.find(g => String(g.pk) === String(this.pk)))
      if (realGroup && realGroup !== this) {
        runInAction(() => {
          realGroup.savedMarkers = this.savedMarkers
        })
      }
    }

    // Fetch details for markers that have place_id (to ensure we have latest data from Google Places)
    if (dataStore && dataStore.poi) {
      for (const marker of this.savedMarkers) {
        const hasPlaceId = !!(marker.place_id || marker.placeId)
        const hasPosition = marker.position && marker.position.latitude != null && marker.position.longitude != null
        // Call ensureMarkerDetails ONLY for markers with a valid place_id
        if (hasPosition && hasPlaceId) {
          // Fire and forget - fetch details in background
          dataStore.poi.ensureMarkerDetails(marker).catch(() => {
            // Ignore errors
          })
        }
      }
    }
  }

  loadSavedMarker (remote) {
    let local = _.find(this.savedMarkers, { id: remote.id })
    const remoteProperties = _.pick(remote, SAVED_MARKER_PROPERTIES)
    if (!local) {
      local = new SavedMarker(remoteProperties)
    } else {
      // Update existing observable properties manually (extendObservable doesn't update existing observables)
      local.name = remoteProperties.name || ''
      local.description = remoteProperties.description || ''
      local.address = remoteProperties.address || ''
      local.color_id = remoteProperties.color || 1
      local.position.longitude = remoteProperties.longitude
      local.position.latitude = remoteProperties.latitude
      // Update POI bucket flags (these are observables and need to be updated for reactivity)
      local.is_general = !!remoteProperties.is_general
      local.is_competitor = !!remoteProperties.is_competitor
      local.is_ally = !!remoteProperties.is_ally
      local.icon_url = remoteProperties.icon_url || null
      local.types = remoteProperties.types || null
      local.place_id = remoteProperties.place_id || null
      local.additional_info = remoteProperties.additional_info
      local.notes = remoteProperties.notes
      local.contact_detail = remoteProperties.contact_detail
      local.status = remoteProperties.shape_status
      local.tag_ids = remoteProperties.tag_ids
      if (remoteProperties.is_owner !== undefined) local.is_owner = remoteProperties.is_owner
    }
    local.targetGroupId = this.pk

    return local
  }

  get selection () {
    // check for new analysis when a target group is selected
    this.loadPyramids()
    // this.loadAnalysis()
    this.error = null
    return {
      type: selectionTypes.TARGET_GROUP,
      item: this
    }
  }

  findBoundary (id) {
    return _.find(this.boundaries, (el) => el.remote_db_id === id) || _.find(this.zipCodeBoundaries, (el) => el.remote_db_id === id)
  }

  @catchError async exportKml (kmlFormat, allShapes) {
    await api.getTargetGroupKML(this.pk, { kmz_file: !kmlFormat, all_shapes: allShapes })
    return this.loadKML()
  }

  @catchError async loadKML (attempt = 0) {
    this.fetchingKml = true
    const response = await api.getTargetGroupAnalysis(this.pk)
    const data = response.data.kml_data

    // if status not_calculated call method again
    // without incrementing attempt value
    if (data.status === asyncStatuses.NOT_CALCULATED) {
      return setTimeout(
        () => { this.loadKML(attempt) },
        attempt < 10 ? 1000 : 5000
      )
    }

    if (data.status === asyncStatuses.PROCESSING) {
      if (attempt < 32) {
        setTimeout(
          () => { this.loadKML(attempt + 1) },
          attempt < 10 ? 1000 : 5000
        )
      } else { // timed out, show message
        this.fetchingKml = false
        throw new Error(i18next.t('GLOBAL.KML_TIMEOUT'), { interpolation: { escapeValue: false } })
      }
    } else if (!data.shapefile || data.status === asyncStatuses.FAILED) {
      this.fetchingKml = false
      throw new Error(i18next.t('GLOBAL.KML_FAILED'))
    } else { // successful
      this.fetchingKml = false
      const url = data.shapefile
      const name = url.substring(url.lastIndexOf('/') + 1, url.length)
      // responseType: 'blob' is required - without it axios decodes the response
      // as UTF-8 text, which silently corrupts binary .kmz downloads (.kml survives
      // since it's already text).
      const res = await api.getFile(url, { responseType: 'blob' })
      const file = new window.File([res.data], name, { type: res.headers['content-type'] })
      FileSaver.saveAs(file, name)
    }
  }

  // if the selected year is > 2024 and no pop data
  // we poll the task id until we get a result
  @catchError
  async loadSignaturePolygonAnalysis (taskId, attempt = 0, cancelToken = null) {
    if (!taskId) return
    const token = cancelToken || (selectionStore && selectionStore.cancelSource ? selectionStore.cancelSource.token : null)
    if (token && token.reason) return // Abort early

    const config = token ? { cancelToken: token } : {}
    const res = await api.getSignaturePolygonPopulatonTaskPolling(taskId, config)
    const data = res.data
    if (data.status === 'PENDING') {
      // retry logic here
      console.log('Analysis is still processing. Retrying...')
      this.handleSignaturePolygonAnalysisPending(taskId, attempt, token)
    }
    if (data.status === 'COMPLETE') {
      console.info('Analysis complete. Updating signature geopolygon data.')
      this.handleSignaturePolygonAnalysisComplete(data.result)
    }
    if (data.status === 'FAILED' || data.status === 'ERROR') {
      this.handleSignaturePolygonAnalysisFailed()
    }
  }

  handleSignaturePolygonAnalysisPending (taskId, attempt, cancelToken) {
    this.analysis = new ProcessingTargetGroupAnalysis()
    this.retryLoadSignaturePolygonAnalysis(attempt, taskId, cancelToken)
  }

  handleSignaturePolygonAnalysisFailed () {
    this.handleEmptyTargetGroup()
  }

  retryLoadSignaturePolygonAnalysis (attempt, taskId, cancelToken) {
    const delay = Math.min(1000 * Math.pow(2, attempt), 5000)
    if (attempt < 32) {
      setTimeout(() => {
        if (cancelToken && cancelToken.reason) return
        this.loadSignaturePolygonAnalysis(taskId, attempt + 1, cancelToken)
      }, delay)
    } else {
      this.handleSignaturePolygonAnalysisFailed()
    }
  }

  handleSignaturePolygonAnalysisComplete (population_datas) {
    // this.population_datas = population_datas // Store the result (could be a dict or a single result)
    this.analysis = new RegionAnalysis()
    this.analysis.load(population_datas, this.geoJSON)
    uiStore.setDataTypeSelectOption('dissolved', false, false)
    // if (selectionStore.item === this && rightSidebarStore) {
    //   rightSidebarStore.fetchPopulationDataForTargetGroup(this.pk, this.name)
    // }
  }

  pickPopulationDataForCurrentYear (rawPd) {
    if (rawPd === null || rawPd === undefined) return null
    if (Array.isArray(rawPd)) return rawPd[0] || null
    if (typeof rawPd !== 'object') return rawPd
    const currentYearKey = String(dataStore.defaultYear.result)
    const currentYearEntry = rawPd[currentYearKey]
    const ds = dataStore.defaultDsName || 'landscan'
    if (currentYearEntry && Array.isArray(currentYearEntry)) {
      const match = currentYearEntry.find(d => d.type === ds)
      if (match) return match
      if (currentYearEntry[0]) return currentYearEntry[0]
    }
    const keys = Object.keys(rawPd)
    const validKey = keys.find(k => {
      const entry = rawPd[k]
      return Array.isArray(entry) && entry.length > 0 && entry.some(d => d && d.Pyramids)
    })
    const bestKey = validKey || keys[0]
    if (bestKey && rawPd[bestKey]) {
      const entry = rawPd[bestKey]
      if (Array.isArray(entry)) {
        const match = entry.find(d => d.type === ds)
        if (match) return match
        return entry[0] || null
      }
    }
    return null
  }

  landscanLatestYear () {
    const year = Number(dataStore && dataStore.landscanLatestYear)
    return Number.isFinite(year) ? year : LANDSCAN_LATEST_YEAR
  }

  pickLandscanPopulationForYear (rawPd, year) {
    if (!rawPd) return null
    const yearKey = String(year != null ? year : this.landscanLatestYear())
    if (Array.isArray(rawPd)) {
      return rawPd.find(d => d && (d.type === 'landscan' || d.sum_pop != null || d.sum != null)) || rawPd[0] || null
    }
    if (typeof rawPd !== 'object') return null
    const yearEntry = rawPd[yearKey] || Object.values(rawPd).find(v => Array.isArray(v) && v.length > 0)
    if (!Array.isArray(yearEntry) || yearEntry.length === 0) return null
    return yearEntry.find(d => d && (d.type === 'landscan' || d.sum_pop != null || d.sum != null)) || yearEntry[0] || null
  }

  @computed get landscanLatestPopulation () {
    const year = this.landscanLatestYear()
    const regions = [...(this.geopolygons || []), ...(this.boundaries || []), ...(this.zipCodeBoundaries || [])]
    return regions.reduce((total, region) => {
      const entry = this.pickLandscanPopulationForYear(region && region.population_datas, year)
      let sum = entry && (entry.sum_pop != null ? entry.sum_pop : (entry.sum != null ? entry.sum : entry.total_population))
      if (sum == null && region && region.analysis && region.analysis.sum != null) {
        sum = region.analysis.sum
      }
      return total + (typeof sum === 'number' ? sum : Number(sum) || 0)
    }, 0)
  }

  @action sortAdditionalInfoItem (event) {
    const sorted_items = move(this.additional_info, event)
    this.addAdditionalInfoItem = sorted_items
  }

  @action setPopulationDatas (population_datas) {
    this.population_datas = population_datas
  }

  async exportExcel () {
    await this.analysis.exportExcel(this.name)
  }

  async exportCSV () {
    await this.analysis.exportCSV(this.name, this)
  }

  @catchError async loadPyramids () {
    if (this.analysis.valid && !this.analysis.loading) {
      return
    }
    const wkt = new Wkt.Wkt()
    const response = await api.getTargetGroupAnalysis(this.pk)
    this.setPopulationDatas(response.data.population_datas)
    this.analysis = new RegionAnalysis()
    if (response.data.signature_geopolygon && response.data.signature_geopolygon.polygon) {
      this.geoJSON = wkt.read(response.data.signature_geopolygon.polygon).toJson()
    } else {
      this.geoJSON = null
    }
    const popData = response.data.population_datas || {}
    const popLandscan = this.pickLandscanPopulationForYear(popData, this.landscanLatestYear())
    this.analysis.load(popLandscan, this.geoJSON)
  }

  // Loads analysis for this target group. Since this analysis is updated
  // asynchronously, poll the server until the analysis status shows
  // that the computation has succeeded or failed.
  @catchError async loadAnalysis (attempt = 0, cancelToken = null) {
    const token = cancelToken || (selectionStore && selectionStore.cancelSource ? selectionStore.cancelSource.token : null)
    if (token && token.reason) return // Abort early if cancelled

    // if (attempt === 0) {
    //   uiStore.setProcessing(true)
    // }
    try {
      const config = token ? { cancelToken: token } : {}
      const response = await api.getTargetGroupAnalysis(this.pk, config)
      const data = response.data.signature_geopolygon
      const taskId = response.data.task_id
      if (!data) {
        this.handleEmptyTargetGroup()
        return
      }
      // sig poly polling can happen but shouldn't block UI
      switch (data.status) {
        case asyncStatuses.PROCESSING:
          this.handleProcessing(attempt, token)
          break
        case asyncStatuses.FAILED:
          this.handleFailed()
          break
        case asyncStatuses.NOT_CALCULATED:
          this.handleNotCalculated(response, attempt, token)
          break
        default:
          this.handleSuccess(data, taskId, response.data, token)
          break
      }
    } catch (error) {
      console.error('Error loading analysis:', error)
      this.handleFailed()
    }
  }

  handleProcessing (attempt, cancelToken) {
    this.analysis = new ProcessingTargetGroupAnalysis()
    this.retryLoadAnalysis(attempt, cancelToken)
  }

  handleFailed () {
    this.analysis = new RegionAnalysis()
    this.analysis.load()
    uiStore.setDataTypeSelectOption('dissolved', false, false)
    // uiStore.setProcessing(false)
  }

  handleNotCalculated (response, attempt, cancelToken) {
    if (response.data.geo_polygons.length > 0 || response.data.admin_boundaries.length > 0) {
      this.analysis = new ProcessingTargetGroupAnalysis()
      this.retryLoadAnalysis(attempt, cancelToken)
    } else {
      this.handleEmptyTargetGroup()
    }
  }

  /**
   * True when value matches retrieve-target-group shape:
   * { "2018": [{ Pyramids: [...] }], "2019": [...], ... }
   */
  isYearKeyedPyramidPopulationDatas (pd) {
    if (pd == null || typeof pd !== 'object' || Array.isArray(pd)) return false
    return Object.keys(pd).some((k) => {
      const bucket = pd[k]
      return Array.isArray(bucket) &&
        bucket[0] &&
        bucket[0].Pyramids &&
        bucket[0].Pyramids.length > 0
    })
  }

  // resolvePopulationPayloadForAnalysis (signaturePolygonData, fullTargetGroupResponse) {
  //   const fromRetrieve = fullTargetGroupResponse && fullTargetGroupResponse.population_datas
  //   if (this.isYearKeyedPyramidPopulationDatas(fromRetrieve)) {
  //     return fromRetrieve
  //   }
  //   return signaturePolygonData.population_data
  // }

  handleSuccess (data, taskId, fullTargetGroupResponse = null, cancelToken) {
    if (taskId) {
      console.info('Analysis is still processing. Retrying with task ID...')
      const wkt = new Wkt.Wkt()
      this.geoJSON = wkt.read(data.polygon).toJson()
      this.loadSignaturePolygonAnalysis(taskId, 0, cancelToken)
      return
    }
    uiStore.setDataTypeSelectOption('dissolved', false, false)
    // const rawPd = this.resolvePopulationPayloadForAnalysis(data, fullTargetGroupResponse)
    this.dissolved_population_datas = data.population_data
    if (!_.isNil(data.population_data)) {
      const wkt = new Wkt.Wkt()
      this.geoJSON = wkt.read(data.polygon).toJson()
      this.analysis = new RegionAnalysis()
      // this.analysis.load
      this.analysis.load(data.population_data, this.geoJSON)
      uiStore.setProcessing(false)
      uiStore.setDataTypeSelectOption('dissolved', false, false)
      return
    }
    this.handleEmptyTargetGroup()
  }

  handleEmptyTargetGroup () {
    this.analysis = new EmptyTargetGroupAnalysis()
    uiStore.setDataTypeSelectOption('dissolved', false, false)
  }

  retryLoadAnalysis (attempt, cancelToken) {
    const delay = Math.min(1000 * Math.pow(2, attempt), 5000)
    if (attempt < 32) {
      setTimeout(() => {
        if (cancelToken && cancelToken.reason) return
        this.loadAnalysis(attempt + 1, cancelToken)
      }, delay)
    } else {
      this.handleFailed()
    }
  }

  async deleteCurrentItemMarkersIfActive (item) {
    const poi = dataStore && dataStore.poi
    if (!poi) return
    const selectedItem = selectionStore && selectionStore.item
    if (selectedItem !== item) return
    try {
      await poi.deleteCurrentBucketMarkersFromGroup(this)
    } catch (err) {
      console.warn('Failed to delete markers for removed item:', err)
    }
  }
}

export default TargetGroup
